import { useState, useEffect } from 'react';
import {
  fetchTourPackagesFromSanity,
  sortTourPackages,
  type SanityTourPackage,
} from '../lib/sanityTourPackages';

export type { SanityTourPackage } from '../lib/sanityTourPackages';

interface UseTourPackagesResult {
  loading: boolean;
  error: string | null;
  packages: SanityTourPackage[];
}

export function useTourPackages(initialPackages: SanityTourPackage[] = []): UseTourPackagesResult {
  const [loading, setLoading] = useState(initialPackages.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [packages, setPackages] = useState<SanityTourPackage[]>(() => sortTourPackages(initialPackages));

  useEffect(() => {
    let cancelled = false;

    const run = () => {
      fetchTourPackagesFromSanity()
        .then(data => {
          if (!cancelled && data.length > 0) {
            setPackages(data);
          }
        })
        .catch(err => {
          if (!cancelled) setError((err as Error).message ?? 'Failed to load packages');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    // Defer the Sanity refresh until the browser is idle so it stays OFF the
    // initial critical path (it was chaining after the JS bundles and delaying
    // load). Pages render immediately from the static/loader fallback; live
    // package data swaps in a moment later.
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    const handle = ric ? ric(run, { timeout: 2000 }) : window.setTimeout(run, 1200);

    return () => {
      cancelled = true;
      const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (ric && cic) cic(handle as number);
      else clearTimeout(handle as number);
    };
  }, []);

  return { loading, error, packages };
}
